import { useCallback, useRef, useSyncExternalStore } from "react";
import type { LastBotCore } from "../core/LastBotCore";
import type { LastBotState } from "../core/state";

/**
 * React hook: subscribes a component to a selected slice of core state via
 * `useSyncExternalStore`. Re-renders only when the underlying core state changes.
 *
 * Selectors do not need to be reference-stable — the hook holds the latest selector
 * in a ref, so passing an inline arrow each render is fine:
 *
 *   const messages = useCoreState(core, s => s.thread?.messages ?? []);
 *
 * The hook also caches the most recent snapshot by core-state identity, so a
 * selector that returns a fresh fallback each call (e.g. `?? []`) won't trip
 * `useSyncExternalStore`'s "getSnapshot should be cached" warning or cause a
 * re-render loop — repeated calls within the same state revision return the
 * same cached `T` reference.
 */
export const useCoreState = <T>(
  core: LastBotCore,
  selector: (_state: LastBotState) => T,
): T => {
  const selectorRef = useRef(selector);
  selectorRef.current = selector;

  // Cache the most recent snapshot keyed by BOTH core state and selector
  // identity. A selector that closes over changing props (e.g. a `role`
  // filter) must produce a fresh value when its identity changes even if
  // core state hasn't moved, otherwise the hook returns a stale slice
  // until the next state mutation.
  const cacheRef = useRef<{
    state: LastBotState;
    selector: (_state: LastBotState) => T;
    value: T;
  } | null>(null);

  const subscribe = useCallback(
    (onChange: () => void) => core.subscribe(() => onChange()),
    [core],
  );

  const getSnapshot = useCallback((): T => {
    const state = core.getState();
    const cached = cacheRef.current;
    if (
      cached &&
      cached.state === state &&
      cached.selector === selectorRef.current
    ) {
      return cached.value;
    }
    const value = selectorRef.current(state);
    cacheRef.current = { state, selector: selectorRef.current, value };
    return value;
  }, [core]);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};
